import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ListChecks, Plus, Trash2, Check, Sparkles } from "lucide-react";

export default function Habits() {
  const { t } = useI18n();
  const [habits, setHabits] = useState([]);
  const [title, setTitle] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = () => api.get("/habits").then(({ data }) => setHabits(data.habits || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (t, reason = "", frequency = "daily") => {
    if (!t.trim()) return;
    try { await api.post("/habits", { title: t, reason, frequency }); setTitle(""); load(); toast.success("Habit added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (id) => { await api.post(`/habits/${id}/complete`); load(); };
  const del = async (id) => { await api.delete(`/habits/${id}`); load(); };

  const suggest = async () => {
    setLoadingSug(true);
    try { const { data } = await api.post("/habits/suggest"); setSuggestions(data.suggestions || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoadingSug(false); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("habits.title")}</h1>
      <p className="mt-2 max-w-lg text-muted-foreground">{t("habits.subtitle")}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <div className="flex flex-1 min-w-64 gap-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(title)}
                 placeholder={t("habits.inputPh")} data-testid="habit-input" className="rounded-full" />
          <Button onClick={() => add(title)} className="rounded-full gap-1" data-testid="add-habit-button"><Plus className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
        <Button variant="outline" onClick={suggest} disabled={loadingSug} className="rounded-full gap-2" data-testid="suggest-habits-button">
          <Sparkles className="h-4 w-4" strokeWidth={1.5} /> {loadingSug ? t("habits.thinking") : t("habits.suggest")}
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-4 space-y-2 rounded-3xl border border-border/60 bg-accent/30 p-4">
          <p className="text-sm font-medium text-primary">{t("habits.ideas")}</p>
          {suggestions.map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-2xl bg-card p-3">
              <div><p className="text-sm font-medium">{s.title}</p><p className="text-xs text-muted-foreground">{s.reason}</p></div>
              <Button size="sm" variant="ghost" className="rounded-full text-primary" onClick={() => add(s.title, s.reason, s.frequency)} data-testid={`accept-suggestion-${i}`}>{t("habits.add")}</Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
        {habits.length === 0 && <p className="text-sm text-muted-foreground">{t("habits.empty")}</p>}
        {habits.map((h, i) => {
          const done = (h.completions || []).includes(today);
          return (
            <motion.div key={h.habit_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.05 }}
                        className="ambient-shadow group flex items-center gap-4 rounded-3xl border border-border/60 bg-card p-5">
              <button onClick={() => toggle(h.habit_id)} data-testid={`toggle-habit-${h.habit_id}`}
                      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300 ${done ? "border-sage bg-sage text-white" : "border-border text-transparent hover:border-primary"}`}>
                <Check className="h-5 w-5" strokeWidth={2.5} />
              </button>
              <div className="flex-1">
                <p className={`font-medium ${done ? "line-through text-muted-foreground" : ""}`}>{h.title}</p>
                {h.reason && <p className="text-xs text-muted-foreground">{h.reason}</p>}
                <p className="mt-1 text-xs text-primary">{(h.completions || []).length} {t("habits.completions")} · {h.frequency}</p>
              </div>
              <button onClick={() => del(h.habit_id)} className="text-muted-foreground opacity-0 group-hover:opacity-100" data-testid={`delete-habit-${h.habit_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
